import type { Quote } from '@client/model';

export interface InvestmentBounds {
  min: number;
  max: number;
}

/**
 * Extracts investment min values from quotes, filtering out invalid values
 */
export function extractInvestmentMins(quotes: Quote[]): number[] {
  return quotes
    .map(q => q.investmentMin)
    .filter((value): value is number => typeof value === 'number' && value > 0);
}

/**
 * Extracts investment max values from quotes, filtering out invalid values
 */
export function extractInvestmentMaxs(quotes: Quote[]): number[] {
  return quotes
    .map(q => q.investmentMax)
    .filter((value): value is number => typeof value === 'number' && value > 0);
}

/**
 * Calculates the most restrictive investment bounds from a set of quotes
 * Returns the highest minimum and lowest maximum to satisfy all quotes
 */
export function calculateInvestmentBounds(
  quotes: Quote[],
  fallbackBounds: InvestmentBounds = { min: 10000, max: 200000 },
): InvestmentBounds {
  const investmentMins = extractInvestmentMins(quotes);
  const investmentMaxs = extractInvestmentMaxs(quotes);

  if (investmentMins.length === 0 || investmentMaxs.length === 0) {
    return fallbackBounds;
  }

  return {
    min: Math.max(...investmentMins),
    max: Math.min(...investmentMaxs),
  };
}

